import { useEffect, useState } from "react";
import { Box, Typography, useTheme, Button, Drawer, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import AgGridTable from "../../components/table/AgGridTable";
import { ColDef, GridOptions, ICellRendererParams } from "ag-grid-community";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import cisco_dark from "../../assets/cisco-white.svg";
import cisco_assets from "../../assets/cisco-assets.svg";
import juniper_dark from "../../assets/juniper-white.svg";
import juniperLogo from "../../assets/juniper.svg";
import paloalto_dark from "../../assets/paloalto-white.svg";
import paloalto_assets from "../../assets/paloalto-assets.svg";
import arista_assets from "../../assets/arista-assets.svg";
import Aws_img from "../../assets/baseVersion/aws.svg";
import AWS_DARK from "../../assets/baseVersion/AWS_DARK.svg"
import "./ResourceReportCustomise.css";

interface ResourceReportCustomiseProps {
  open: boolean;
  onClose: () => void;
  skipApiCall: boolean;
  onFilterDataSaved?: (filterData: any) => void;
  preSelectedResourceIds?: string[];
}

const ResourceReportCustomise = ({
  open,
  onClose,
  onFilterDataSaved,
  preSelectedResourceIds = [],
  skipApiCall
}: ResourceReportCustomiseProps) => {
  console.log("preSelectedResourceIds in ResourceReportCustomise", preSelectedResourceIds);
  const [assetsData, setAssetsData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  // Define default column definition
  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    suppressMovable: false,
  };

  // Define grid options with multi-select functionality
  const gridOptions: GridOptions = {
    defaultColDef,
    rowHeight: 48,
    headerHeight: 48,
    suppressCellFocus: true,
    rowStyle: { cursor: "pointer" },
    suppressRowClickSelection: true,
    rowMultiSelectWithClick: false,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    rowSelection: "multiple" as const,
    
  };

  // Define column definitions for Cloud assets with checkbox selection
  const cloudColumnDefs: ColDef[] = [
    
    {
      headerName: "Select",
      field: 'id',
      minWidth: 80,
      width: 80,
      filter: false,
      sortable: false,
      headerComponent: () => {
        const allSelected = assetsData.length > 0 && assetsData.every(item => item.selected);
        const someSelected = assetsData.some(item => item.selected);
        
        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            width: '100%',
            paddingLeft: '16px'
          }}>
            <input 
              type="checkbox" 
              className={`header-checkbox ${isDarkMode ? 'dark-mode' : ''}`}
              checked={allSelected}
              ref={(input) => {
                if (input) {
                  input.indeterminate = someSelected && !allSelected;
                }
              }}
              onChange={() => {
                const shouldSelectAll = !allSelected;
                setAssetsData(prevData => 
                  prevData.map(item => ({ ...item, selected: shouldSelectAll }))
                );
                setSelectedRows(shouldSelectAll ? assetsData : []);
              }} 
            />
          </div>
        );
      },
      cellRenderer: (params: ICellRendererParams) => {
        return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
          width: '100%',
          paddingLeft: '16px'
        }}>
          <input 
            type="checkbox" 
            className={`custom-checkbox ${isDarkMode ? 'dark-mode' : ''}`}
            checked={params.data.selected || false} 
            onChange={() => {
              // Update the local state
              setAssetsData(prevData => {
                const updatedData = prevData.map(item => 
                  item.id === params.data.id 
                    ? { ...item, selected: !item.selected }
                    : item
                );
                
                // Update selectedRows based on the new data
                const customSelectedRows = updatedData.filter(item => item.selected);
                setSelectedRows(customSelectedRows);
                
                return updatedData;
              });
              
              // Refresh the entire grid to ensure all checkboxes are updated
              setTimeout(() => {
                if (params.api) {
                  params.api.refreshCells({ force: true });
                }
              }, 0);
            }} 
          />
        </div>
        );
      },    
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    },
    {
      field: "resource_name",
      headerName: "Name",
      filter: false,
      sortable: true,
      minWidth: 300,
      width: 330,
      cellRenderer: (params: ICellRendererParams) => {
        const criticalityColors: any = {
          critical: "firebrick",
          high: "orange",
          low: "limegreen",
          default: "goldenrod",
        };

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <div
              style={{
                minWidth: "4px",
                width: "4px",
                height: "32px",
                backgroundColor:
                  criticalityColors[params?.data?.criticality?.toLowerCase()] ||
                  "goldenrod",
                borderRadius: "2px",
                marginRight: "8px",
                cursor: "pointer",
                flexShrink: 0,
                position: "relative",
                zIndex: 1,
              }}
            ></div>
            <Typography
              title={params.data.resource_name}
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                cursor: "pointer",
                flexGrow: 1,
                minWidth: 0,
                maxWidth: "160px",
              }}
            >
              {params.data.resource_name}
            </Typography>
          </Box>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
    {
      field: "cloud",
      headerName: "Vendor",
      filter: false,
      sortable: true,
      minWidth: 180,
      width: 180,
      cellRenderer: (params: ICellRendererParams) =>
        params.data.vendor?.toLowerCase() === "cisco" ? (
          <img
            src={isDarkMode ? cisco_dark : cisco_assets}
            alt="Cisco"
            style={{ width: "60px", height: "20px" }}
          />
        ) : params.data.vendor?.toLowerCase() === "juniper" ? (
          <img
            src={isDarkMode ? juniper_dark : juniperLogo}
            alt="Juniper"
            style={{ width: "60px", height: "20px" }}
          />
        ) : params.data.vendor?.toLowerCase() === "paloalto" ||
          params.data.vendor?.toLowerCase() === "Palo Alto" ? (
          <img
            src={isDarkMode ? paloalto_dark : paloalto_assets}
            alt="Palo Alto"
            style={{ width: "60px", height: "20px" }}
          />
        ) : params.data.vendor?.toLowerCase() === "arista" ? (
          <img src={arista_assets} alt="Arista" />
        ) : params.data.cloud?.toLowerCase() === "amazon web services" ? (
          <img src={isDarkMode?AWS_DARK:Aws_img} alt="AWS" />
        ) : params.data.cloud ? (
          <Typography
            title={params.data.cloud}
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {params.data.cloud}
          </Typography>
        ) : params.data.vendor ? (
          <Typography
            title={params.data.vendor}
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {params.data.vendor}
          </Typography>
        ) : null,
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
    {
      field: "resources_type",
      headerName: "Resource Type",
      filter: false,
      sortable: true,
      minWidth: 200,
      width: 240,
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
  ];

  const fetchAssetsData = async () => {
    try {
      setIsLoading(true);
      const endpoint = END_POINTS.DEVICE_LIST_CLOUD;
      const payload = { type: 1 };
      const response = await axiosInstance.post(endpoint, payload);
      if (response.data) {
        const cloudResources = response.data?.data?.cloud_resource || [];
  
        // Add selected: false to each object
        const updatedAssets = cloudResources.map((asset: any) => ({
          ...asset,
          selected: false,
        }));
  
        setAssetsData(updatedAssets);
      }
    } catch (error) {
      console.error("Error fetching cloud assets data:", error);
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    fetchAssetsData();
  }, [skipApiCall]);

  // Pre-select resources based on preSelectedResourceIds
  useEffect(() => {
    if (assetsData.length > 0) {
      // Always update the selection state based on preSelectedResourceIds
      const updatedAssets = assetsData.map(asset => ({
        ...asset,
        selected: preSelectedResourceIds.includes(asset.id)
      }));
      
      setAssetsData(updatedAssets);
      
      const preSelectedResources = updatedAssets.filter((resource: any) => 
        preSelectedResourceIds.includes(resource.id)
      );
      setSelectedRows(preSelectedResources);
    }
  }, [preSelectedResourceIds, assetsData.length]);


  const handleSelectionChange = () => {
    // This function is for AG Grid's built-in selection, but we're using custom checkboxes
    // So we'll update selectedRows based on our custom checkbox state
    const customSelectedRows = assetsData.filter(item => item.selected);
    setSelectedRows(customSelectedRows);
  };

  const handleDone = async () => {
    try {
      // Create filter data structure with resource IDs
      const filterData = {
        resources_list: selectedRows || [],
        resource_ids: selectedRows.map(row => row.id) || []
      };
      // Call the callback to save the filter data
      onFilterDataSaved?.(filterData);
      onClose();
    } catch (error) {
      console.error("Error in handleDone:", error);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '80%',
          maxWidth: '800px',
          backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            padding: '16px 20px',
            borderBottom: '1px solid #E2E2F2',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: isDarkMode ? '#FFFFFF' : '#000000',
              fontWeight: 600,
            }}
          >
            Resource Report Customization
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <AgGridTable
          key={`resource-customise-${preSelectedResourceIds.join(',')}`}
          data={assetsData}
          columnDefs={cloudColumnDefs}
          isLoading={isLoading}
          showSearch={true}
          height="calc(100vh - 200px)"
          gridOptions={gridOptions}
          checkboxSelection={false}
          headerCheckboxSelection={false}
          isRowSelectable={false}
          onSelectionChange={handleSelectionChange}
        />
      </Box>

        {/* Action buttons */}
        <Box sx={{
          padding: '16px 20px',
          borderTop: '1px solid #E2E2F2',
          display: "flex",
          gap: 2,
          justifyContent: "flex-end"
        }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDone}
            disabled={selectedRows.length === 0}
            sx={{ minWidth: 100 }}
          >
            Done ({selectedRows.length})
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ResourceReportCustomise;